<!-- src/Components/NewsfeedReactionPicker.vue -->

<template>
  <Teleport v-if="portal" to="body">
    <div
      @mouseenter="emitHoverChange(true)"
      @mouseleave="emitHoverChange(false)"
      class="fixed bg-white border border-zinc-200 rounded-full px-2 py-1 shadow-[0_12px_30px_rgba(15,23,42,0.35)] flex items-center gap-2 select-none z-[5000]"
      :style="portalStyle"
    >
      <button
        v-for="r in reactions"
        :key="r.type"
        type="button"
        class="relative group w-10 h-10 rounded-full flex items-center justify-center
               bg-white shadow-sm transform
               hover:-translate-y-1 hover:scale-110
               transition-transform duration-150
               focus:outline-none"
        @click.stop="handleSelect(r.type)"
      >
        <img
          :src="r.icon"
          :alt="r.label"
          class="w-10 h-10 pointer-events-none transform transition-transform duration-150 group-hover:scale-110"
        />

        <span
          class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2
                 px-2 py-[3px] rounded-xl bg-black/70 text-[11px] leading-tight
                 text-white whitespace-nowrap
                 opacity-0 translate-y-1
                 group-hover:opacity-100 group-hover:translate-y-0
                 transition-all duration-150"
        >
          {{ r.label }}
        </span>
      </button>
    </div>
  </Teleport>

  <div
    @mouseenter="emitHoverChange(true)"
    @mouseleave="emitHoverChange(false)"
    v-else
    :class="[
      positionClass,
      'bg-white border border-zinc-200 rounded-full px-2 py-1 shadow-[0_12px_30px_rgba(15,23,42,0.35)] flex items-center gap-2 select-none z-[1200]'
    ]"
  >
    <button
      v-for="r in reactions"
      :key="r.type"
      type="button"
      class="relative group w-10 h-10 rounded-full flex items-center justify-center
             bg-white shadow-sm transform
             hover:-translate-y-1 hover:scale-110
             transition-transform duration-150
             focus:outline-none"
      @click.stop="handleSelect(r.type)"
    >
      <img
        :src="r.icon"
        :alt="r.label"
        class="w-10 h-10 pointer-events-none transform transition-transform duration-150 group-hover:scale-110"
      />

      <span
        class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2
               px-2 py-[3px] rounded-xl bg-black/70 text-[11px] leading-tight
               text-white whitespace-nowrap
               opacity-0 translate-y-1
               group-hover:opacity-100 group-hover:translate-y-0
               transition-all duration-150"
      >
        {{ r.label }}
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  positionClass: {
    type: String,
    default: 'absolute bottom-full mb-2 left-0',
  },
  portal: {
    type: Boolean,
    default: false,
  },
  portalAnchorSelector: {
    type: String,
    default: '.nf-picker-anchor:hover',
  },
});

const emit = defineEmits(['select', 'hover-change']);
const viewportTick = ref(0);

const resolveAnchorRect = () => {
  if (typeof window === 'undefined') return null;
  const anchor = document.querySelector(props.portalAnchorSelector);
  if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return null;
  return anchor.getBoundingClientRect();
};

const portalStyle = computed(() => {
  // force recompute on viewport changes
  void viewportTick.value;
  const rect = resolveAnchorRect();
  if (!rect) {
    return {
      top: '80px',
      left: '500px',
      transform: 'translate(-100%, -100%)',
    };
  }
  return {
    top: `${Math.max(8, rect.top - 8)}px`,
    left: `${Math.max(8, rect.right)}px`,
    transform: 'translate(-100%, -100%)',
  };
});

const emitHoverChange = (value) => {
  emit('hover-change', !!value);
};

const handleSelect = (type) => {
  emit('select', type);
};

const likeIcon  = new URL('@/assets/media/images/newsfeed-like-icon.png',  import.meta.url).href;
const loveIcon  = new URL('@/assets/media/images/newsfeed-love-icon.png',  import.meta.url).href;
const careIcon  = new URL('@/assets/media/images/newsfeed-care-icon.png',  import.meta.url).href;
const hahaIcon  = new URL('@/assets/media/images/newsfeed-haha-icon.png',  import.meta.url).href;
const wowIcon   = new URL('@/assets/media/images/newsfeed-wow-icon.png',   import.meta.url).href;
const sadIcon   = new URL('@/assets/media/images/newsfeed-sad-icon.png',   import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry-icon.png', import.meta.url).href;

const reactions = [
  { type: 'like',  label: 'Like',  icon: likeIcon },
  { type: 'love',  label: 'Love',  icon: loveIcon },
  { type: 'care',  label: 'Care',  icon: careIcon },
  { type: 'haha',  label: 'Haha',  icon: hahaIcon },
  { type: 'wow',   label: 'Wow',   icon: wowIcon },
  { type: 'sad',   label: 'Sad',   icon: sadIcon },
  { type: 'angry', label: 'Angry', icon: angryIcon },
];

const onViewportMove = () => {
  viewportTick.value += 1;
};

onMounted(() => {
  if (typeof window === 'undefined') return;
  window.addEventListener('scroll', onViewportMove, true);
  window.addEventListener('resize', onViewportMove);
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', onViewportMove, true);
  window.removeEventListener('resize', onViewportMove);
});
</script>

